import {Box, Flex, Text, Title} from 'exsportia-components'
import React, {FC} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {useStoreOrder} from '../../api/store'
import {useVenueById} from '../../api/venue/hooks'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {ORDER_STATUS_LOCALE, useCurrencyFormat} from './store-shared'

export const StoreOrderPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const format = useCurrencyFormat()
  const {saleId} = useParams<{saleId: string}>()
  const {data: order, isError} = useStoreOrder(saleId)
  // The ORDER's venue, not the URL's — the collect-at card must name where this order is
  // actually collected (WBA-4607).
  const {data: venue} = useVenueById(order?.venueId)
  if (!order) {
    // Loading covers the retry window after a paid redirect — the webhook that
    // promotes the draft can be seconds behind the customer.
    return (
      <GridLayout>
        <Box width='100%' py='48px'>
          <Flex
            gap='16px'
            alignItems='center'
            flexDirection='column'
          >
            <Text
              text={isError
                ? l('store-order-not-found', {caseAction: 'capitalize'})
                : l('store-finalizing', {caseAction: 'capitalize'})}
              settings={{fontWeight: 600, color: 'rgba(0, 16, 61, 0.64)'}}
            />
            {isError && (
              <Button
                type='secondary'
                settings={{width: 'max-content'}}
                onClick={() => navigate('../orders')}
              >
                {l('store-orders', {caseAction: 'capitalize'})}
              </Button>
            )}
          </Flex>
        </Box>
      </GridLayout>
    )
  }
  // What happens next, in the customer's terms — the page a paid checkout
  // lands on, so it has to answer "what now?" without them asking.
  const NEXT_STEPS: Record<string, string> = {
    PLACED: 'store-next-placed',
    PROCESSING: 'store-next-placed',
    READY_FOR_PICKUP: 'store-next-ready',
    COMPLETED: 'store-next-completed',
    CANCELLED: 'store-next-cancelled',
  }
  const nextSteps = [
    l(NEXT_STEPS[order.orderStatus] || 'store-next-placed', {caseAction: 'capitalize'}),
    ...(order.isPaid || order.orderStatus === 'CANCELLED' ? [] : [l('store-next-unpaid', {caseAction: 'capitalize'})]),
  ].join(' ')
  const banner = order.orderStatus === 'CANCELLED'
    ? {bg: '#F9E4E2', color: '#B3261E'}
    : order.orderStatus === 'READY_FOR_PICKUP' || order.orderStatus === 'COMPLETED'
    ? {bg: '#E2F2E9', color: '#1E7A4C'}
    : {bg: '#F7EED8', color: '#8A6210'}
  return (
    <GridLayout>
    <Box width='100%'>
      <Box py='8px'>
        <Title
          styleType='h1'
          text={`${l('store-order', {caseAction: 'capitalize'})} ${order.reference || order.uid}`}
        />
      </Box>
      <Box
        p='12px'
        borderRadius='12px'
        backgroundColor={banner.bg}
      >
        <Text
          text={`${l(ORDER_STATUS_LOCALE[order.orderStatus], {caseAction: 'capitalize'})}${
            order.isPaid ? '' : ` · ${l('store-pay-at-pickup', {caseAction: 'capitalize'})}`
          }`}
          settings={{color: banner.color, fontWeight: 600}}
        />
        <Box mt='4px'>
          <Text
            text={nextSteps}
            settings={{color: banner.color, fontSize: '13px'}}
          />
        </Box>
      </Box>
      <Box mt='16px'>
        {order.lines.map((line, index) => (
          <Flex
            key={`${line.stockItemId || ''}${index}`}
            py='8px'
            gap='12px'
            justifyContent='space-between'
          >
            <Text
              text={`${line.label}${line.variantLabel ? ` (${line.variantLabel})` : ''}`}
            />
            {/* Quantity × ITEM price, as the till shows it — the line total read as the item's
                price when more than one was ordered (WBA-4605). */}
            <Text
              text={`${line.quantity} × ${format(line.price)}`}
              settings={{fontWeight: 600, whiteSpace: 'nowrap'}}
            />
          </Flex>
        ))}
        {order.promoCode && Number(order.promoDiscount) > 0 && (
          <Flex
            py='8px'
            gap='12px'
            justifyContent='space-between'
          >
            <Text
              text={l('promo_line', {code: order.promoCode})}
              settings={{color: 'primary', fontWeight: 600}}
            />
            <Text
              text={`-${format(Number(order.promoDiscount))}`}
              settings={{color: 'primary', fontWeight: 600, whiteSpace: 'nowrap'}}
            />
          </Flex>
        )}
      </Box>
      <Flex
        py='16px'
        justifyContent='space-between'
      >
        <Title
          styleType='h3'
          text={l('store-total', {caseAction: 'capitalize'})}
        />
        <Title
          styleType='h3'
          text={format(order.totalPrice)}
        />
      </Flex>
      {order.orderStatus !== 'CANCELLED' && (
        <Box pb='12px'>
          <Text
            text={order.isPaid
              ? l('store-paid-online', {caseAction: 'capitalize'})
              : l('store-pay-at-pickup', {caseAction: 'capitalize'})}
            settings={{fontWeight: 700, color: order.isPaid ? '#1E7A4C' : '#B3261E'}}
          />
        </Box>
      )}
      {/* Where to collect — the card MBA's order page carries (Dima 2026-08-19). */}
      {venue?.address && (
        <Box
          p='12px'
          mb='16px'
          borderRadius='12px'
          backgroundColor='rgba(0, 16, 61, 0.04)'
        >
          <Text
            text={l('store-collect-at', {caseAction: 'capitalize'})}
            settings={{fontWeight: 700}}
          />
          <Box mt='4px'>
            <Text text={venue.name} settings={{fontWeight: 600}} />
          </Box>
          <Text
            text={venue.address.allData?.formattedAddress || venue.address.addressDetails || ''}
            settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.64)'}}
          />
          {venue.address.latLng && (
            <Box mt='8px'>
              <Button
                type='secondary'
                settings={{width: 'max-content'}}
                onClick={() => window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${venue.address?.latLng.lat},${venue.address?.latLng.lng}`,
                  '_blank',
                )}
              >
                {l('store-get-directions', {caseAction: 'capitalize'})}
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Flex
        gap='8px'
        flexDirection='column'
      >
        <Button
          type='primary'
          onClick={() => navigate('../store')}
        >
          {l('store-explore-goods', {caseAction: 'capitalize'})}
        </Button>
        {/* Back to the list this order lives in (WBA-4603). */}
        <Button
          type='secondary'
          onClick={() => navigate('../orders')}
        >
          {l('store-orders', {caseAction: 'capitalize'})}
        </Button>
        {/* "Go to", not "back to": this navigates to the ORDER's venue, which is not
            necessarily the venue the customer came from (WBA-4606). */}
        <Button
          type='secondary'
          onClick={() => navigate(`/${order.venueId}`)}
        >
          {l('store-go-to-venue', {caseAction: 'capitalize'})}
        </Button>
      </Flex>
    </Box>
    </GridLayout>
  )
}
